import { type ElementType, type Ref, useEffect, useRef, useState } from "react";

import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";

import { cx, styled } from "simplydo/react/styled";

import { Icon } from "../icon/Icon";
import { boxShadowFor } from "../utils";
import {
  ScrollButton,
  ScrollControls,
  StyledTable,
  TableContainer,
  TableViewport,
} from "./Table.styles";
import type { TableProps } from "./Table.types";

type Overflow = { start: boolean; end: boolean };

const TableComponent = (
  props: TableProps & { ref?: Ref<HTMLTableElement> },
) => {
  const {
    ref,
    id,
    as,
    celled,
    striped,
    compact,
    basic,
    collapsing,
    sortable,
    selectable,
    singleLine,
    fixed,
    padded,
    size,
    definition,
    structured,
    unstackable,
    textAlign,
    verticalAlign,
    className,
    style,
    stickyLastColumn,
    children,
  } = props;

  const viewportRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState<Overflow>({
    start: false,
    end: false,
  });
  const [stickyEndWidth, setStickyEndWidth] = useState(0);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = viewport;
      const start = scrollLeft > 1;
      const end = scrollLeft + clientWidth < scrollWidth - 1;
      setOverflow((prev) =>
        prev.start === start && prev.end === end ? prev : { start, end },
      );

      const table = viewport.firstElementChild as HTMLTableElement | null;
      const lastCell = table?.rows[0]?.lastElementChild as HTMLElement | null;
      setStickyEndWidth(stickyLastColumn ? (lastCell?.offsetWidth ?? 0) : 0);
    };

    update();
    viewport.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(viewport);
    if (viewport.firstElementChild) {
      observer.observe(viewport.firstElementChild);
    }

    return () => {
      viewport.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [stickyLastColumn]);

  const scrollBy = (direction: 1 | -1) => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const visibleWidth = Math.max(viewport.clientWidth - stickyEndWidth, 0);
    viewport.scrollBy({
      left: direction * visibleWidth * 0.8,
      behavior: "smooth",
    });
  };

  const Component = (as || "table") as ElementType;
  const shadow = boxShadowFor({ elevation: "small" });

  return (
    <TableContainer
      className={cx(
        className,
        basic && "basic",
        collapsing && "is-collapsing",
        overflow.start && "can-scroll-start",
        overflow.end && "can-scroll-end",
      )}
      style={style}
      $basic={basic}
      $shadow={shadow}
      $stickyEnd={`${stickyEndWidth}px`}
    >
      <TableViewport ref={viewportRef}>
        <StyledTable
          ref={ref}
          id={id}
          as={Component}
          className={cx(
            celled && "celled",
            striped && "is-striped",
            compact && "compact",
            basic && "basic",
            collapsing && "is-collapsing",
            sortable && "is-sortable",
            selectable && "is-selectable",
            singleLine && "is-single-line",
            fixed && "fixed",
            size,
            definition && "is-definition",
            structured && "structured",
            unstackable && "unstackable",
            stickyLastColumn && "has-sticky-last-column",
          )}
          $celled={celled}
          $compact={compact}
          $basic={basic}
          $padded={padded}
          $size={size}
          $fixed={fixed}
          $textAlign={textAlign}
          $verticalAlign={verticalAlign}
        >
          {children}
        </StyledTable>
      </TableViewport>
      {overflow.start || overflow.end ? (
        <ScrollControls>
          <div>
            <ScrollButton
              type="button"
              className={cx("is-start", overflow.start && "is-visible")}
              aria-label="Scroll table left"
              tabIndex={overflow.start ? 0 : -1}
              aria-hidden={!overflow.start}
              onClick={() => scrollBy(-1)}
              $endOffset={`${stickyEndWidth + 8}px`}
            >
              <Icon icon={ArrowLeftIcon} />
            </ScrollButton>
            <ScrollButton
              type="button"
              className={cx("is-end", overflow.end && "is-visible")}
              aria-label="Scroll table right"
              tabIndex={overflow.end ? 0 : -1}
              aria-hidden={!overflow.end}
              onClick={() => scrollBy(1)}
              $endOffset={`${stickyEndWidth + 8}px`}
            >
              <Icon icon={ArrowRightIcon} />
            </ScrollButton>
          </div>
        </ScrollControls>
      ) : null}
    </TableContainer>
  );
};

export const Table = styled(TableComponent)``;
